import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toFetchableUrl } from "../../../utils/imageFetch";

export const isPdf = (url = "", name = "") =>
  /\.pdf($|\?)/i.test(url) || /\.pdf$/i.test(name);

/**
 * Previews a post document.
 *
 * Accepts either:
 *   url  — a stored document (saved post, edit mode, PostViewing)
 *   file — a File the user just picked, before it has been uploaded
 *
 * PDFs render inline in an iframe. Office formats (DOC, DOCX, PPT, PPTX)
 * can't be rendered by browsers, so they fall back to a file card.
 */
export default function DocumentPreview({
  url = null,
  file = null,
  filename = null,
  title = null,
  height = "aspect-[4/5]",
  actions = null,
}) {
  const { t } = useTranslation();
  const [objectUrl, setObjectUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  const name = filename || file?.name || t("draft.document", "Document");
  const pdf = isPdf(url || "", name);

  const [isSmallScreen, setIsSmallScreen] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );

  useEffect(() => {
    const onResize = () => setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!pdf) return;
    if (isSmallScreen && url) return;

    let cancelled = false;
    let created = null;

    (async () => {
      try {
        setFailed(false);

        // A locally picked file has no URL yet — object URL is the only option.
        // For a stored URL we fetch and re-wrap the bytes as application/pdf so
        // the browser renders them inline.
        //
        // NOTE: the fetch branch exists only because the storage account serves
        // documents with `Content-Disposition: attachment`. Once that's fixed
        // backend-side, this whole effect can be replaced with:
        //     const src = file ? URL.createObjectURL(file) : url;
        // and the CORS dependency (toFetchableUrl) disappears with it.
        const blob = file || (await (await fetch(toFetchableUrl(url))).blob());
        created = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));

        if (!cancelled) setObjectUrl(created);
      } catch (err) {
        console.error("Document preview failed:", err);
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [url, file, pdf]);

  return (
    <div className="rounded-lg overflow-hidden border border-black-40">
      {/* Header — mirrors the image block's toolbar */}
      <div className="flex items-center justify-between gap-3 p-2 bg-black-20">
        <span className="font-bold text-black-800 truncate">
          {title || t("draft.preview")}
        </span>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>
        )}
      </div>

      {/* Body */}
      {!pdf ? (
        <div className="flex items-center gap-3 p-4">
          <span className="text-2xl">📄</span>
          <span className="text-black-600">
            {t("draft.noPreview")}
          </span>
        </div>
      ) : failed ? (
        <div className="flex items-center gap-3 p-4">
          <span className="text-2xl">📄</span>
          <span className="text-black-600">
            {t("draft.previewFailed")}
          </span>
        </div>
      ) : isSmallScreen && url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 p-4 hover:bg-black-20 transition-colors"
        >
          <span className="text-2xl">📄</span>
          <span className="font-bold text-black-800">
            {t("draft.openDocument", "Open document")}
          </span>
        </a>
      ) : objectUrl ? (
        isSmallScreen ? (
          url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 hover:bg-black-20 transition-colors"
            >
              <span className="text-2xl">📄</span>
              <span className="font-bold text-black-800">
                {t("draft.openDocument", "Open document")}
              </span>
            </a>
          ) : (
            <a
              href={objectUrl}
              download={name}
              className="flex items-center gap-3 p-4 hover:bg-black-20 transition-colors"
            >
              <span className="text-2xl">📄</span>
              <span className="font-bold text-black-800">
                {t("draft.openDocument", "Open document")}
              </span>
            </a>
          )
        ) : (
          <div className="mx-auto w-full max-w-[600px]">
            <iframe
              src={`${objectUrl}#view=Fit`}
              title={name}
              className={`w-full ${height} max-h-[70vh] bg-white`}
            />
          </div>
        )
      ) : (
        <div className={`w-full ${height} flex items-center justify-center bg-white`}>
          <div className="w-8 h-8 border-4 border-primary-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}